import { RefreshCw, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatEUR } from '../../lib/format';

interface ValidationIssue {
  code: string;
  type: 'BLOCKING' | 'WARNING';
  message: string;
}
interface DraftInvoice {
  id: string;
  invoice_number?: string;
  flow: string | null;
  einvoice_status: string | null;
  total_ht: string | number;
  total_vat: string | number;
  total_ttc: string | number;
  validation: { errors: ValidationIssue[]; warnings: ValidationIssue[] };
  canSubmit: boolean;
}

/** Screen 64 — Invoice batch validation. ACCOUNTING + MANAGER + OWNER. */
export default function InvoiceBatchValidation() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [drafts, setDrafts] = useState<DraftInvoice[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = async () => {
    try {
      const { data } = await apiClient.get<{ data: DraftInvoice[] }>(
        `/einvoice/drafts?date=${date}`
      );
      setDrafts(data.data ?? []);
      // Pre-select every invoice that passes validation.
      setSelected(new Set((data.data ?? []).filter((d) => d.canSubmit).map((d) => d.id)));
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    void load();
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await apiClient.post<{ data: { submitted: number; blocked: number } }>(
        '/einvoice/submit-batch',
        { invoiceIds: ids },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('einvoice.batch.submitted', {
          submitted: data.data.submitted,
          blocked: data.data.blocked,
        }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const totalSelectedTTC = drafts
    .filter((d) => selected.has(d.id))
    .reduce((s, d) => s + Number(d.total_ttc), 0);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3 border-b pb-4">
        <h1 className="text-xl font-semibold tracking-tight">{t('einvoice.batch.title')}</h1>
        <Input
          type="date"
          className="h-8 w-auto"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4" />
          {t('einvoice.batch.reload')}
        </Button>
        <Button
          variant="success"
          size="sm"
          className="ml-auto"
          onClick={submit}
          disabled={selected.size === 0}
        >
          <Send className="h-4 w-4" />
          {t('einvoice.batch.submitAll', {
            count: selected.size,
            amount: formatEUR(totalSelectedTTC),
          })}
        </Button>
      </header>

      <p className="text-sm text-muted-foreground">{t('einvoice.batch.intro')}</p>

      <ul className="space-y-2">
        {drafts.map((d) => (
          <li
            key={d.id}
            className={`border rounded p-3 bg-card ${!d.canSubmit ? 'border-destructive/40 bg-destructive/10' : ''}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary disabled:opacity-50"
                disabled={!d.canSubmit}
                checked={selected.has(d.id)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(d.id);
                  else next.delete(d.id);
                  setSelected(next);
                }}
              />
              <div className="flex-1">
                <div className="font-medium">
                  {d.invoice_number ?? d.id.slice(0, 8)}
                  <span className="ml-2 text-xs text-muted-foreground">{d.flow ?? '—'}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('einvoice.batch.totals', {
                    ht: formatEUR(Number(d.total_ht)),
                    vat: formatEUR(Number(d.total_vat)),
                    ttc: formatEUR(Number(d.total_ttc)),
                  })}
                </div>
                {d.validation.errors.length > 0 && (
                  <ul className="mt-2 text-xs text-destructive list-disc ml-5">
                    {d.validation.errors.map((e, i) => (
                      <li key={i}>
                        <strong>{e.code}</strong> — {e.message}
                      </li>
                    ))}
                  </ul>
                )}
                {d.validation.warnings.length > 0 && (
                  <ul className="mt-1 text-xs text-warning list-disc ml-5">
                    {d.validation.warnings.map((w, i) => (
                      <li key={i}>
                        <strong>{w.code}</strong> — {w.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </li>
        ))}
        {drafts.length === 0 && (
          <li className="text-sm text-muted-foreground">{t('einvoice.batch.empty', { date })}</li>
        )}
      </ul>
    </div>
  );
}
